import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CommonGlobalService, CommonGlobalSettingsService, FileType, RootState, usePersistantState, UUID } from '@umanforge/common';
import SendFileInput from '@common_components/inputs/sendFileInput.component';
import { useTranslation } from 'react-i18next';
import { EventService } from 'services/events.service';
import EventDocumentImage from './document/eventDocumentImage.component';
import EventDocumentPdf from './document/eventDocumentPdf.component';
import EventDocumentText from './document/eventDocumentText.component';
import EventDocumentOther from './document/eventDocumentOther.component';
import { EventDocument } from 'interfaces/document.interface';
import { ToastService } from 'services/toast.service';
import { VisualisationService } from 'services/visualisation.service';
import { KEY } from 'constants/local_storage_key.constant';


interface DocumentsSectionProps {
  eventId: UUID;
  isViewMode?: boolean;
  isArchived?: boolean;
  referenceEventId?: string;
  referenceEventTitle?: string;
  referenceEventDocuments?: EventDocument[];
  isCreationMode?: boolean;
  isAdminOfEventManager?: boolean;
  isParticipant?: boolean;
  setParentCurrentDocuments?: React.Dispatch<React.SetStateAction<EventDocument[]>>;
  onPendingDocumentChange?: ( hasPendingDocuments: boolean ) => void;
  onTriggerDocumentsSave?: () => void;
}

const toastService: ToastService = new ToastService( 'DocumentsSection' );

const DocumentsSection: React.FC<DocumentsSectionProps> = ( {
  eventId,
  isViewMode,
  isArchived,
  referenceEventId,
  referenceEventTitle,
  referenceEventDocuments = [],
  isCreationMode = false,
  isAdminOfEventManager,
  isParticipant,
  setParentCurrentDocuments,
  onPendingDocumentChange,
  onTriggerDocumentsSave
} ) => {
  const { t } = useTranslation();
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();
  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const [ selectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const [ currentDocuments, setCurrentDocuments ] = useState<EventDocument[]>( [] );
  useEffect( () => {
    if ( setParentCurrentDocuments ) {
      setParentCurrentDocuments( currentDocuments );
    }
  }, [ currentDocuments, setParentCurrentDocuments ] );
  const [ deleteDocuments, setDeleteDocuments ] = useState<UUID[]>( [] );
  const [ newDocuments, setNewDocuments ] = useState<File[]>( [] );
  const [ referenceDocuments, setReferenceDocuments ] = useState<EventDocument[]>( referenceEventDocuments );
  const [ pendingCreationDocuments, setPendingCreationDocuments ] = useState<File[]>( [] );
  const [ temporaryDocumentPreviews, setTemporaryDocumentPreviews ] = useState<Array<{
    id: string;
    file: File;
    url: string;
    name: string;
  }>>( [] );

  const [ imageVersion, setImageVersion ] = useState<number>( 0 );

  const [ maxFileSize, setMaxFileSize ] = useState<number>( 0 );

  const [ renderFilePicker, setRenderFilePicker ] = useState<number>( 0 );

  const isAdmin =  isAdminOfEventManager || ( me != undefined && selectedSectorId != undefined && CommonGlobalService.isAdminOfSector( me, selectedSectorId ) );
  const isInputDisabled = isArchived || isViewMode || !( isAdmin || isParticipant );

  useEffect( () => {
    init();
  }, [] );

  useEffect( () => {
    if ( !isCreationMode && eventId && currentDocuments.length === 0 ) {
      refreshDocuments();
    }
  }, [ isCreationMode ] );

  useEffect( () => {
    if ( eventId && !isCreationMode ) {
      refreshDocuments();
    }
  }, [ eventId, isCreationMode ] );

  useEffect( () => {
    if ( referenceEventDocuments && referenceEventDocuments.length > 0 ) {
      setReferenceDocuments( referenceEventDocuments );
    } else if ( referenceEventId ) {
      loadReferenceDocuments();
    }
  }, [ referenceEventId, referenceEventDocuments ] );

  useEffect( () => {
    if ( isCreationMode && typeof window !== 'undefined' ) {
      const pendingDocumentsHandler: PendingDocumentsHandler = {
        getPendingDocuments: () => { return newDocuments; },
        clearPendingDocuments: () => {
          setNewDocuments( [] );
          setPendingCreationDocuments( [] );
          cleanupTemporaryDocuments();
        },
        loadDocumentsAfterCreation: ( newEventId: string ) => {
          return loadDocumentsAfterCreation( newEventId ).then( () => { return undefined; } );
        }
      };

      ( window as WindowWithPendingDocuments )._pendingDocumentsForEvent = pendingDocumentsHandler;
    }

    return () => {
      if ( typeof window !== 'undefined' ) {
        delete ( window as WindowWithPendingDocuments )._pendingDocumentsForEvent;
      }
    };
  }, [ isCreationMode, newDocuments, temporaryDocumentPreviews ] );

  useEffect( () => {
    if ( isCreationMode && typeof window !== 'undefined' ) {
      const typedWindow = window as WindowWithPendingDocuments;

      if ( typedWindow._temporaryDocuments && typedWindow._temporaryDocuments.length > 0 ) {
        setPendingCreationDocuments( typedWindow._temporaryDocuments );
        setNewDocuments( typedWindow._temporaryDocuments );
      }

      if ( typedWindow._temporaryDocumentPreviews && typedWindow._temporaryDocumentPreviews.length > 0 ) {
        setTemporaryDocumentPreviews( typedWindow._temporaryDocumentPreviews );
      }
    }
  }, [ isCreationMode ] );

  const init = async () => {
    await refreshMaxFileSize();

    if ( isCreationMode ) {
      const typedWindow = window as WindowWithPendingDocuments;
      if ( typedWindow._temporaryDocuments && typedWindow._temporaryDocuments.length > 0 ) {
        setPendingCreationDocuments( typedWindow._temporaryDocuments );
        setNewDocuments( typedWindow._temporaryDocuments );
      }

      if ( typedWindow._temporaryDocumentPreviews && typedWindow._temporaryDocumentPreviews.length > 0 ) {
        setTemporaryDocumentPreviews( typedWindow._temporaryDocumentPreviews );
      }

    } else {
      await refreshDocuments();
    }
  };

  const loadReferenceDocuments = async () => {
    if ( referenceEventId && token ) {
      try {
        const response: EventDocument[] = [];
        if ( isViewMode ) {
          response.push( ...( await VisualisationService.getDocumentsByEventId( referenceEventId, visualisationToken, dispatch ) ) );
        } else {
          response.push( ...( await EventService.getDocumentsByEventId( referenceEventId, token, dispatch ) ) );
        }
        setReferenceDocuments( response );
      } catch ( error ) {
        console.error( 'Erreur lors de la récupération des documents de référence:', error );
        setReferenceDocuments( [] );
      }
    }
  };

  const addDeletedDocument = ( fileId: UUID ) => {
    const index = deleteDocuments.findIndex( documentId => { return  fileId === documentId; } );
    if ( index != -1 ) {
      deleteDocuments.splice( index, 1 );
      setDeleteDocuments( [ ...deleteDocuments ] );
    } else {
      setDeleteDocuments( [ ...new Set( [ ...deleteDocuments, fileId ] ).values() ] );
    }
  };

  const addNewDocument = ( files: File[] ) => {
    if ( isCreationMode ) {
      setNewDocuments( [ ...newDocuments, ...files ] );
      const updatedPendingDocs = [ ...pendingCreationDocuments, ...files ];
      setPendingCreationDocuments( updatedPendingDocs );

      const previews = files.map( file => {
        const randomId = Math.random().toString( 36 ).substring( 2, 15 );

        const url = URL.createObjectURL( file );
        return {
          id: randomId,
          file: file,
          url: url,
          name: file.name
        };
      } );

      setTemporaryDocumentPreviews( [ ...temporaryDocumentPreviews, ...previews ] );

      const typedWindow = window as WindowWithPendingDocuments;
      typedWindow._temporaryDocuments = updatedPendingDocs;
      typedWindow._temporaryDocumentPreviews = [ ...temporaryDocumentPreviews, ...previews ];
    } else {
      setNewDocuments( files );
    }
  };

  const renderDocument = ( doc: EventDocument, canDelete: boolean, canRotate: boolean ) => {
    switch ( CommonGlobalService.getFileType( doc.originalName ) ) {
    case FileType.IMAGE:
      return (
        <EventDocumentImage
          key={ doc.id }
          isViewMode={ isViewMode }
          file={ doc }
          isDeleted={ deleteDocuments.some( documentId => { return doc.id === documentId; } ) }
          canDelete={ canDelete }
          canRotate={ canRotate }
          onRotationSaved={refreshDocuments}
          imageVersion={imageVersion}
          deleteFile={ addDeletedDocument }
        />
      );
    case FileType.PDF:
      return (
        <EventDocumentPdf
          key={ doc.id }
          isViewMode={ isViewMode }
          file={ doc }
          isDeleted={ deleteDocuments.some( documentId => { return doc.id === documentId; } ) }
          canDelete={ canDelete }
          deleteFile={ addDeletedDocument }
        />
      );
    case FileType.TEXT:
      return (
        <EventDocumentText
          key={ doc.id }
          isViewMode={ isViewMode }
          file={ doc }
          isDeleted={ deleteDocuments.some( documentId => { return doc.id === documentId; } ) }
          canDelete={ canDelete }
          deleteFile={ addDeletedDocument }
        />
      );
    default:
      return (
        <EventDocumentOther
          key={ doc.id }
          isViewMode={ isViewMode }
          file={ doc }
          isDeleted={ deleteDocuments.some( documentId => { return doc.id === documentId; } ) }
          canDelete={ canDelete }
          deleteFile={ addDeletedDocument }
        />
      );
    }
  };

  const renderTemporaryDocument = ( tempDoc: { id: string; file: File; url: string; name: string } ) => {
    const fileType = CommonGlobalService.getFileType( tempDoc.name );

    const documentLike: EventDocument = {
      id: tempDoc.id,
      eventId: eventId,
      originalName: tempDoc.name,
      extension: tempDoc.name.split( '.' ).pop() || '',
      url: tempDoc.url,
      thumbnailUrl: tempDoc.url
    };

    switch ( fileType ) {
    case FileType.IMAGE:
      return (
        <EventDocumentImage
          key={ tempDoc.id }
          file={ documentLike }
          isDeleted={ false }
          canDelete={ true }
          canRotate={ true }
          imageVersion={imageVersion}
          deleteFile={ () => { return removeTemporaryDocument( tempDoc.id ); } }
        />
      );
    case FileType.PDF:
      return (
        <EventDocumentPdf
          key={ tempDoc.id }
          file={ documentLike }
          isDeleted={ false }
          canDelete={ true }
          deleteFile={ () => { return removeTemporaryDocument( tempDoc.id ); } }
        />
      );
    case FileType.TEXT:
      return (
        <EventDocumentText
          key={ tempDoc.id }
          file={ documentLike }
          isDeleted={ false }
          canDelete={ true }
          deleteFile={ () => { return removeTemporaryDocument( tempDoc.id ); } }
        />
      );
    default:
      return (
        <EventDocumentOther
          key={ tempDoc.id }
          file={ documentLike }
          isDeleted={ false }
          canDelete={ true }
          deleteFile={ () => { return removeTemporaryDocument( tempDoc.id ); } }
        />
      );
    }
  };

  const removeTemporaryDocument = ( tempDocId: string ) => {
    const updatedPreviews = temporaryDocumentPreviews.filter( doc => { return doc.id !== tempDocId; } );
    setTemporaryDocumentPreviews( updatedPreviews );

    const docToRemove = temporaryDocumentPreviews.find( doc => { return doc.id === tempDocId; } );
    if ( docToRemove ) {
      URL.revokeObjectURL( docToRemove.url );

      const updatedPendingDocs = pendingCreationDocuments.filter( file => {
        return !( file.name === docToRemove.file.name && file.size === docToRemove.file.size );
      } );
      setPendingCreationDocuments( updatedPendingDocs );

      const updatedNewDocs = newDocuments.filter( file => {
        return !( file.name === docToRemove.file.name && file.size === docToRemove.file.size );
      } );
      setNewDocuments( updatedNewDocs );

      if ( typeof window !== 'undefined' ) {
        const typedWindow = window as WindowWithPendingDocuments;
        typedWindow._temporaryDocuments = updatedPendingDocs;
        typedWindow._temporaryDocumentPreviews = updatedPreviews;
      }
    }
  };

  const refreshDocuments = async () => {
    if ( isCreationMode ) {
      return;
    }

    setRenderFilePicker( renderFilePicker + 1 );
    setNewDocuments( [] );
    setDeleteDocuments( [] );

    try {
      const response: EventDocument[] = [];
      if ( isViewMode ) {
        response.push( ...( await VisualisationService.getDocumentsByEventId( eventId, visualisationToken, dispatch ) ) );
      } else {
        response.push( ...( await EventService.getDocumentsByEventId( eventId, token, dispatch ) ) );
      }

      setCurrentDocuments( response );
      setImageVersion( prev => { return prev + 1; } );
    } catch ( error ) {
      console.error( 'Error refreshing documents:', error );
    }
  };

  const refreshMaxFileSize = async () => {
    let updatedMaxFileSize = 0;
    if ( !isViewMode ) {
      updatedMaxFileSize = await CommonGlobalSettingsService.getMaxFileSize( token, dispatch );
    }
    setMaxFileSize( updatedMaxFileSize );
  };

  useEffect( () => {
    if ( onPendingDocumentChange ) {
      const hasPendingChanges = ( newDocuments.length > 0 || deleteDocuments.length > 0 );
      onPendingDocumentChange( hasPendingChanges );
    }
  }, [ newDocuments, deleteDocuments, onPendingDocumentChange ] );

  const onSaveDocuments = useCallback( async () => {
    try {
      const promisesNewDocuments = newDocuments.map( doc => { return EventService.postDocumentOnEvent( eventId, doc, token, dispatch ); } );
      const promisesDeleteDocuments = deleteDocuments.map( doc => { return EventService.deleteDocument( doc, token, dispatch ); } );

      const response = await Promise.all( [
        ...promisesNewDocuments,
        ...promisesDeleteDocuments
      ] );

      if ( response.includes( true ) ) {
        toastService.showSuccess( t( 'common:documents_updated' ) );
        await refreshDocuments();
      }
    } catch ( error ) {
      console.error( 'Error saving documents:', error );
      toastService.showError( t( 'common:error_saving_documents' ) );
    }
  }, [ newDocuments, deleteDocuments, eventId, token, dispatch, refreshDocuments, t ] );

  useEffect( () => {
    if ( onTriggerDocumentsSave ) {
      ( window as WindowWithPendingDocuments & { _handleSaveDocuments?: () => void } )._handleSaveDocuments = onSaveDocuments;
    }
  }, [ onTriggerDocumentsSave, onSaveDocuments ] );

  const cleanupTemporaryDocuments = () => {
    temporaryDocumentPreviews.forEach( tempDoc => {
      URL.revokeObjectURL( tempDoc.url );
    } );

    setTemporaryDocumentPreviews( [] );
    setPendingCreationDocuments( [] );

    if ( typeof window !== 'undefined' ) {
      const typedWindow = window as WindowWithPendingDocuments;
      typedWindow._temporaryDocuments = [];
      typedWindow._temporaryDocumentPreviews = [];
    }
  };

  const loadDocumentsAfterCreation = async ( newEventId: string ) => {
    try {
      if ( !newEventId ) {
        console.error( 'loadDocumentsAfterCreation: ID d\'événement invalide' );
        return Promise.reject( 'ID d\'événement invalide' );
      }

      await new Promise( resolve => { setTimeout( resolve, 500 ); } );

      const response = await EventService.getDocumentsByEventId( newEventId, token, dispatch );

      setCurrentDocuments( response );
      setImageVersion( prev => { return prev + 1; } );

      cleanupTemporaryDocuments();

      return Promise.resolve( true );
    } catch ( error ) {
      console.error( 'Erreur lors du chargement des documents après création:', error );
      return Promise.reject( error );
    }
  };

  return (
    <div className="font-texte min-h-full h-full w-full">
      <div className="flex flex-col gap-4 w-full h-full items-center bg-white rounded-lg shadow-lg p-4">

        {
          !( isArchived || isViewMode ) &&
            <div className='flex w-full h-full max-h-[350px]'>
              <SendFileInput
                disable={isArchived || isViewMode || isInputDisabled}
                key={ renderFilePicker }
                maxFileSize={ maxFileSize }
                clearAfterChange={ isCreationMode }
                multipleFiles
                label=''
                onChange={ addNewDocument }
              />
            </div>
        }

        <div className='flex flex-col w-full h-full gap-8'>
          {/* Section pour les documents de l'événement de référence */}
          {referenceDocuments && referenceDocuments.length > 0 && (
            <div className="w-full">
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-50 rounded-lg">
                <div className="text-blue-600 font-medium">
                  {t( 'translation:reference_event_documents' )}
                </div>
                {referenceEventTitle && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-blue-700 font-medium px-2 py-1 rounded bg-blue-100">
                      {referenceEventTitle}
                    </span>
                  </div>
                )}
              </div>
              <div className={`
                flex flex-wrap
                h-fit w-full gap-4 px-2 py-4
                justify-center overflow-x-visible overflow-y-auto
                border-l-4 border-blue-200
                `}
              >
                <div className="flex flex-wrap gap-4 px-2 py-4 justify-center pl-3">
                  {
                    referenceDocuments.map( doc => {
                      return renderDocument( doc, false, false );
                    } )
                  }
                </div>
              </div>
            </div>
          )}

          {/* Section pour les documents de l'événement actuel */}
          <div className="w-full">
            {referenceDocuments && referenceDocuments.length > 0 && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-orange-50 rounded-lg">
                <div className="text-orange-600 font-medium">
                  {t( 'translation:current_event_documents', 'Documents de l\'événement actuel' )}
                </div>
              </div>
            )}
            <div className={`
              flex flex-wrap
              h-fit w-full gap-4 px-2 py-4
              justify-center overflow-x-visible overflow-y-auto
              ${ referenceDocuments && referenceDocuments.length > 0 ? 'border-l-4 border-orange-200' : '' }
              `}
            >
              {
                ( () => {
                  if ( isCreationMode && temporaryDocumentPreviews.length > 0 ) {
                    return (
                      <div className='flex flex-wrap gap-4 px-2 py-4 justify-center pl-3'>
                        {
                          temporaryDocumentPreviews.map( tempDoc => {
                            return renderTemporaryDocument( tempDoc );
                          } )
                        }
                      </div>
                    );
                  }

                  if ( currentDocuments.length > 0 ) {
                    return (
                      <div className='flex flex-wrap gap-4 px-2 py-4 justify-center pl-3'>
                        {
                          currentDocuments.map( doc => {
                            return renderDocument(
                              doc,
                              !( isArchived || isViewMode ),
                              !( isArchived || isViewMode || isInputDisabled )
                            );
                          } )
                        }
                      </div>
                    );
                  }

                  return (
                    <span className='text-gray-800'>
                      { t( 'common:no_associated_document' ) }
                    </span>
                  );
                } )()
              }
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DocumentsSection;

interface PendingDocumentsHandler {
  getPendingDocuments: () => File[];
  clearPendingDocuments: () => void;
  loadDocumentsAfterCreation: ( newEventId: string ) => Promise<void>;
}

interface WindowWithPendingDocuments extends Window {
  _pendingDocumentsForEvent?: PendingDocumentsHandler;
  _temporaryDocuments?: File[];
  _temporaryDocumentPreviews?: Array<{
    id: string;
    file: File;
    url: string;
    name: string;
  }>;
}

